/**
 * Living Safe Corridor (ADR-065).
 *
 * One visual system carries the whole safe-driving lesson: a broad translucent
 * blue band on the road is where the robot may politely be, and its LENGTH is
 * the robot's permitted speed. Keep right, give way, slow into uncertainty,
 * wait in a pocket, resume when it clears — all of it is one rule the player
 * can see: follow blue, respect amber, wait when blue stops.
 *
 * The corridor is computed in the delivery path frame (s along the route,
 * d positive to the RIGHT of travel) as an interval of d per station of s.
 * Edges come from probing the road network's exact SDF, so the corridor can
 * never cross a curb; static obstacles and stopped road users subtract
 * sub-intervals.
 *
 * Moving road users are predicted along their intent velocity, and their
 * predicted space is CARVED OUT of the band rather than instantly ending it:
 * traffic passing in its own lane makes the corridor lean right and narrow —
 * which is the keep-right lesson, live on the road — and only when nothing
 * viable remains does the corridor end short of the conflict with a waiting
 * pocket. The first version stopped for everything that moved, and play-
 * testing called the waits what they were: unnecessary.
 *
 * Speed is a single law, not three modes: cap = sqrt(2·decel·remaining), so a
 * long corridor means cruise, a shortening one means braking, and a corridor
 * reduced to its waiting pocket means rolling smoothly to a stop inside it.
 *
 * Deterministic pure-ish module (a planner instance holds smoothing/hysteresis
 * state, but everything is a function of the sim inputs). No DOM, no Babylon.
 */

import type { AgentState } from "./agents";
import {
  CORRIDOR_AMBER_CLEAR_DELAY_S,
  CORRIDOR_AMBER_MARGIN_M,
  CORRIDOR_CONFLICT_CLEAR_M,
  CORRIDOR_FLOOR_FRAC,
  CORRIDOR_CURB_CLEAR_M,
  CORRIDOR_DWELL_S,
  CORRIDOR_EXTEND_MPS,
  CORRIDOR_HORIZON_S,
  CORRIDOR_MAX_LEN_M,
  CORRIDOR_POCKET_LEN_M,
  CORRIDOR_PREDICT_STEP_S,
  CORRIDOR_RETRACT_MPS,
  CORRIDOR_RETURN_TOL_M,
  CORRIDOR_LEFT_MAX_M,
  CORRIDOR_RIGHT_MAX_M,
  CORRIDOR_SLIDE_WIDTH_M,
  CORRIDOR_STATIC_CLEAR_M,
  CORRIDOR_STATION_M,
  CORRIDOR_STOP_MARGIN_M,
  CORRIDOR_CAP_RISE_MPS2,
  CORRIDOR_TIGHT_CLEAR_M,
  CORRIDOR_TTC_WINDOW_S,
  CORRIDOR_TURN_LOOK_M,
  CORRIDOR_TURN_TIGHT_RAD,
} from "./constants";
import type { DeliveryPath } from "./path";
import type { RoadNetwork } from "./network";
import type { Obstacle, SafetyState } from "./types";
import type { RobotShape } from "./world";
import { wrapAngle } from "../core/vec";

export type CorridorState =
  | "OPEN_RIGHT"
  | "CAUTION"
  | "WAITING_POCKET"
  | "BLOCKED_RIGHT"
  | "PASSING_STATIC_OBSTACLE"
  | "RETURN_RIGHT"
  | "EMERGENCY_STOP";

/** One cross-section of the corridor: the drivable d-interval at s. */
export interface CorridorStation {
  s: number;
  lo: number;
  hi: number;
}

/** A priority road user's predicted occupied space, for the amber ribbon. */
export interface AmberPath {
  id: string;
  /** Predicted center positions in WORLD space (the agent may cross the route). */
  pts: Array<{ x: number; z: number }>;
  halfWidth: number;
}

export interface CorridorPlan {
  state: CorridorState;
  /** Cross-sections from the robot forward; empty only before the first step. */
  stations: CorridorStation[];
  /** Visible (smoothed) length ahead of the robot (m). */
  length: number;
  /** Waiting pocket, when a conflict ends the corridor. */
  pocket: { s0: number; s1: number; lo: number; hi: number } | null;
  amber: AmberPath | null;
  /** Max speed the corridor permits right now (m/s). */
  speedCap: number;
  /** Path distance from the robot to the binding conflict (m), or null. */
  conflictS: number | null;
  /** The road user being given way to or leaned away from, or null. */
  priorityId: string | null;
  /** Band geometry at the robot, for telemetry and the steering clamp. */
  centerD: number;
  width: number;
  /** True while the band is shifted left of its keep-right target to pass. */
  bulge: boolean;
  /** Research-window neutrality: the painted strip must not lean right. */
  neutral: boolean;
  /** Debug: why the corridor is what it is. */
  reason: string;
}

export interface CorridorInput {
  path: DeliveryPath;
  network: RoadNetwork;
  obstacles: readonly Obstacle[];
  agents: readonly AgentState[];
  robotS: number;
  robotD: number;
  robotX: number;
  robotZ: number;
  robotSpeed: number;
  robotHeading: number;
  shape: RobotShape;
  /** Effective (profile-scaled) limits. */
  decel: number;
  cruise: number;
  governor: SafetyState;
  /**
   * True inside the research encounter (plus a short approach): the
   * corridor becomes a NEUTRAL full-road band that reacts to nothing.
   * Keep-right is itself a side cue, and the pass-side choice against the
   * research pedestrian is the very thing the study measures.
   */
  neutral: boolean;
  /** Pedestrian-style crossing legs (two-stage lefts): centred band there. */
  crossings?: ReadonlyArray<{ s0: number; s1: number }>;
  t: number;
  dt: number;
}

/** Is path progress s inside a crossing leg (with a little approach slack)? */
function inCrossing(crossings: ReadonlyArray<{ s0: number; s1: number }> | undefined, s: number): boolean {
  if (!crossings) return false;
  for (const c of crossings) {
    if (s >= c.s0 - 0.5 && s <= c.s1 + 0.5) return true;
  }
  return false;
}

/** One moving road user's predicted occupancy, as d-cuts per station. */
interface MovingCut {
  lo: number;
  hi: number;
  id: string;
  /** When the agent occupies this space (s from now). */
  tA: number;
}

interface Judged {
  id: string;
  amber: AmberPath;
  /** Station-grid key → cuts. */
  cuts: Map<number, MovingCut[]>;
}

const PROBE_STEP = 0.3;
const PROBE_RANGE = 7;

export class CorridorPlanner {
  /** Road d-intervals are static per station — cache them forever. */
  private readonly roadCache = new Map<number, { lo: number; hi: number }>();
  private lenSmooth = 12;
  /** Cap rise-rate limiter (falls are instant; see plan step 7). */
  private capSmooth = 0;
  private state: CorridorState = "OPEN_RIGHT";
  private stateSince = 0;
  /** Amber stays until its agent has been conflict-free for a beat, so a
   * predicted path flickering across the band edge cannot strobe the cue. */
  private amberId: string | null = null;
  private amberFreeSince = Infinity;
  private lastAmber: AmberPath | null = null;
  /** Bulge bookkeeping for PASSING → RETURN_RIGHT → OPEN. */
  private returnSince: number | null = null;
  /** Last real pocket, kept while its episode lasts (see plan step 6). */
  private lastPocket: CorridorPlan["pocket"] = null;
  /** Per-agent conflict hold: once judged relevant, an agent stays relevant
   * for a beat. The entry gates read the robot speed, the verdict changes the
   * robot speed, and without this the two strobed at the sim rate. */
  private readonly conflictHold = new Map<string, number>();
  /** Per-agent path-projection hints: a windowed search over 60 m of route
   * for EVERY agent EVERY step was the corridor's whole CPU bill. */
  private readonly sHintByAgent = new Map<string, number>();

  plan(input: CorridorInput): CorridorPlan {
    const { path, shape } = input;
    const need = shape.r + CORRIDOR_CURB_CLEAR_M;
    const s0 = input.robotS;

    // --- 1) Hard length limits: route end and forward visibility. ----------
    // A bend the player cannot see around shortens the corridor, which (via
    // the one speed law) IS the slow-approach behaviour — no special case.
    let maxLen = Math.min(CORRIDOR_MAX_LEN_M, Math.max(2, path.length - s0));
    let turn = 0;
    let prevTheta = path.point(s0).theta;
    for (let a = CORRIDOR_STATION_M; a <= CORRIDOR_TURN_LOOK_M; a += CORRIDOR_STATION_M) {
      const th = path.point(s0 + a).theta;
      turn += Math.abs(wrapAngle(th - prevTheta));
      prevTheta = th;
      if (turn > CORRIDOR_TURN_TIGHT_RAD) {
        maxLen = Math.min(maxLen, Math.max(6, a + 2));
        break;
      }
    }

    if (input.neutral) return this.neutralPlan(input, s0, maxLen, need);

    // --- 2) Predict moving road users. --------------------------------------
    const moving: Judged[] = [];
    const staticAgents: AgentState[] = [];
    for (const a of input.agents) {
      if (!a.active || !a.collidable) continue;
      // The research pedestrian is the measured stimulus: the corridor must
      // not react to it, or the cue would decide the pass-side choice the
      // study exists to observe. The safety governor still protects.
      if (a.type === "research_ped") continue;
      const speed = Math.hypot(a.safetyVx, a.safetyVz);
      // Cheap Euclidean gate BEFORE any path projection: an agent that could
      // not reach the corridor within the horizon cannot matter this step.
      const dx = a.x - input.robotX;
      const dz = a.z - input.robotZ;
      const reach = maxLen + speed * CORRIDOR_HORIZON_S + 8;
      if (dx * dx + dz * dz > reach * reach) continue;
      if (speed < 0.15) {
        staticAgents.push(a);
        continue;
      }
      const j = this.judge(a, speed, input, s0, maxLen);
      if (j === "static") staticAgents.push(a);
      else if (j) moving.push(j);
    }

    // Static agents projected ONCE per plan — station() ran a path projection
    // per (station × agent), which multiplied a modest cost by thirteen.
    const staticCuts: Array<{ s: number; d: number; cut: number }> = [];
    for (const a of staticAgents) {
      const hint = this.sHintByAgent.get(a.id);
      const f = input.path.project(a.x, a.z, hint ?? s0, hint === undefined ? 60 : 10);
      this.sHintByAgent.set(a.id, f.s);
      staticCuts.push({ s: f.s, d: f.d, cut: a.radius + agentHalfLen(a) + 0.6 });
    }

    // --- 3) Build stations. --------------------------------------------------
    // Moving road users CARVE the band, they do not automatically end it: the
    // band leans right and narrows while they pass (the keep-right lesson,
    // visible), and only a station left with nothing viable is a conflict.
    //
    // Bulging left past a block is only offered while no moving road user is
    // active near the corridor — it must never invite racing the amber.
    for (const j of moving) this.conflictHold.set(j.id, input.t + 1.2);
    const holdActive = [...this.conflictHold.values()].some((until) => until > input.t);
    const oncomingActive = moving.length > 0 || this.amberId !== null || holdActive;
    // Robot pace used to judge WHEN it reaches each station: never below a
    // walking pace, or a robot already stopped could never be released.
    const vRef = Math.max(input.robotSpeed, 1.2);
    const stations: Array<CorridorStation & { flo: number; fhi: number }> = [];
    let endS = s0 + maxLen;
    let blockedBy: "static" | "moving" | null = null;
    let bindingId: string | null = null;
    let leanId: string | null = null;
    let bulge = false;
    let minGapAhead = Infinity;
    // Stations sit on a WORLD-FIXED grid (plus one at the robot itself).
    // A robot-anchored grid moved under the robot as it drove, so the station
    // a parked car blocked jittered by a full spacing and the corridor end
    // with it — the speed law then ratcheted the robot up to the bumper.
    const grid: number[] = [s0];
    for (
      let g = Math.ceil(s0 / CORRIDOR_STATION_M) * CORRIDOR_STATION_M;
      g <= s0 + maxLen + 1e-6;
      g += CORRIDOR_STATION_M
    ) {
      if (g > s0 + 0.2) grid.push(g);
    }
    for (const s of grid) {
      const st = this.station(s, need, input, staticCuts, moving, s0, vRef, !oncomingActive);
      if (st.band === null) {
        if (st.deadBy !== null) {
          endS = Math.min(endS, s - CORRIDOR_CONFLICT_CLEAR_M);
          blockedBy = "moving";
          bindingId = st.deadBy;
        } else {
          endS = Math.min(endS, s - CORRIDOR_STATIC_CLEAR_M);
          blockedBy = "static";
        }
        break;
      }
      if (st.leanBy !== null && leanId === null) leanId = st.leanBy;
      if (st.bulged) bulge = true;
      if (s - s0 < 8) minGapAhead = Math.min(minGapAhead, st.band.hi - st.band.lo);
      stations.push({ s, lo: st.band.lo, hi: st.band.hi, flo: st.band.flo, fhi: st.band.fhi });
    }
    // Lateral continuity. A drivable corridor cannot teleport sideways: the
    // junction stations used to flip between disjoint window choices, and on
    // screen the ribbon tore into shards. Two passes:
    //
    // BACKWARD (anticipation): a station may WIDEN inside its own free space
    // toward where the next station needs the robot to be — the approach to
    // a pass fans left before the blocker, the way a driver prepares a lane
    // change, instead of the band jumping at the car.
    //
    // FORWARD (reachability): edges may then move at most ~1.3 m per
    // station from the previous band; a station that cannot be reached that
    // way ends the corridor — it was never drivable.
    {
      const minT = CORRIDOR_TIGHT_CLEAR_M * 2;
      const R = 1.3;
      // Widen ONLY where consecutive bands fail to interface, and only by
      // the minimum needed — unbounded anticipation cascaded backward and
      // fanned the whole approach into the left lane twenty metres early.
      for (let i = stations.length - 2; i >= 0; i--) {
        const n = stations[i + 1]!;
        const c = stations[i]!;
        if (c.hi < n.lo - R + 0.5) c.hi = Math.min(c.fhi, n.lo - R + 0.5);
        if (c.lo > n.hi + R - 0.5) c.lo = Math.max(c.flo, n.hi + R - 0.5);
      }
      const clampN = (v: number, lo: number, hi: number): number =>
        Math.min(hi, Math.max(lo, v));
      for (let i = 1; i < stations.length; i++) {
        const p = stations[i - 1]!;
        const c = stations[i]!;
        // Each edge moves at most R per station TOWARD this station's own
        // band, always inside this station's free space — that is what lets
        // the corridor ramp back right after a pass instead of dying beside
        // the blocker it just cleared.
        let lo = clampN(clampN(c.lo, p.lo - R, p.lo + R), c.flo, c.fhi);
        let hi = clampN(clampN(c.hi, p.hi - R, p.hi + R), c.flo, c.fhi);
        if (hi - lo < minT) {
          // No band-to-band ramp: fall back to a pure transition segment —
          // whatever part of this station's free space the robot can reach.
          lo = Math.max(c.flo, p.lo - R);
          hi = Math.min(c.fhi, p.hi + R);
        }
        if (hi - lo >= minT) {
          c.lo = lo;
          c.hi = hi;
        } else {
          endS = Math.min(endS, c.s - CORRIDOR_STATIC_CLEAR_M);
          if (blockedBy === null) blockedBy = "static";
          stations.length = i;
          break;
        }
      }
    }
    endS = Math.max(endS, s0 + 0.5);
    const conflictS = blockedBy === "moving" ? Math.max(0, endS + CORRIDOR_CONFLICT_CLEAR_M - s0) : null;

    // --- 4) Amber, with a short clear-delay so it never strobes. ------------
    // A full conflict (the corridor ENDS for them) always shows amber; a mere
    // lean shows it too — the band visibly bending away from an amber path is
    // exactly the picture "respect amber" needs.
    // Waiting behind a static block FOR opposing traffic also shows that
    // traffic's amber — the player must see WHOM the wait is for.
    const oncomingNearest =
      blockedBy === "static" && moving.length > 0 ? moving[0]!.id : null;
    const amberFor = bindingId ?? leanId ?? oncomingNearest;
    const amberAgent = amberFor !== null ? moving.find((m) => m.id === amberFor) : undefined;
    if (amberAgent) {
      this.amberId = amberAgent.id;
      this.amberFreeSince = Infinity;
      this.lastAmber = amberAgent.amber;
    } else if (this.amberId !== null) {
      if (!Number.isFinite(this.amberFreeSince)) this.amberFreeSince = input.t;
      if (input.t - this.amberFreeSince >= CORRIDOR_AMBER_CLEAR_DELAY_S) {
        this.amberId = null;
        this.lastAmber = null;
      }
    }
    const amber = this.amberId !== null ? this.lastAmber : null;

    // --- 5) Waiting pocket: the last stretch before the corridor's end. -----
    let pocket: CorridorPlan["pocket"] = null;
    const wantPocket = blockedBy !== null;
    if (wantPocket && endS - s0 > 1.5 && stations.length > 0) {
      const p0 = Math.max(s0, endS - CORRIDOR_POCKET_LEN_M);
      // The last surviving station's interval: it already excludes the
      // blocker and every other obstacle, so waiting there is truly safe.
      const at = stations[stations.length - 1]!;
      pocket = { s0: p0, s1: endS, lo: at.lo, hi: at.hi };
      this.lastPocket = pocket;
    } else if (wantPocket && this.lastPocket !== null && this.lastPocket.s1 > s0 - 1) {
      // The corridor has fully retracted onto the robot: keep the pocket it
      // already had rather than conjuring a sliver at its nose.
      pocket = this.lastPocket;
    }
    if (!wantPocket) this.lastPocket = null;

    // --- 6) Length smoothing: retract briskly (the cap must win the race
    // against the robot's own approach), extend readily (resume promptly is
    // one of the five behaviours being taught). The CAP uses the truth. ------
    const truth = Math.max(0.5, endS - s0);
    this.lenSmooth += clamp(
      truth - this.lenSmooth,
      -CORRIDOR_RETRACT_MPS * input.dt,
      CORRIDOR_EXTEND_MPS * input.dt,
    );
    const effLen = Math.min(this.lenSmooth, truth);

    // --- 7) One speed law. ---------------------------------------------------
    // Braking at the COMFORT rate, not the mechanical limit: the corridor
    // teaches a pace a careful driver would choose, and with the crash-stop
    // decel in the formula even a 6 m corridor still granted full cruise.
    const comfort = Math.min(2.2, input.decel * 0.4);
    // The route's end is the CUSTOMER, not a hazard: braking to zero at the
    // door reads as one more unnecessary wait. Arrive at a walking pace and
    // let the arrival logic do the stopping.
    const endIsDest = endS >= path.length - 0.6;
    const margin = endIsDest ? -1.5 : CORRIDOR_STOP_MARGIN_M;
    let speedCap = Math.min(
      input.cruise,
      Math.sqrt(2 * comfort * Math.max(0, effLen - margin)),
    );
    // Narrow gaps ahead slow the robot CONTINUOUSLY in the gap width — the
    // old binary ×0.55 toggle was jerk, but no pre-slow at all sent the robot
    // into the alley squeezes fast enough that the governor fired its harsh
    // emergency stop instead, which is worse jerk.
    if (Number.isFinite(minGapAhead)) {
      const gapFactor = Math.min(1, Math.max(0.55, minGapAhead / 1.0));
      speedCap = Math.min(speedCap, input.cruise * gapFactor);
    }
    // Amendment F: the corridor is ADVISORY. The law slows the robot toward
    // its pocket but never pins it — and never below HALF the player's pace,
    // so waiting is a choice the bay invites, not one the cap enforces. (This
    // also subsumes the old reach-the-pocket creep floor.)
    //
    // The one place with its own pace is a pedestrian CROSSING leg (the
    // two-stage left, ADR-068): the manoeuvre is specified slow — pivot 90°,
    // cross like a pedestrian — and at the ordinary floor the chassis could
    // not track the turn (it swung wide onto the far kerb). Walking-pace
    // band there: never pinned below 1.3, never granted above 2.2.
    // The band covers the approach AND the whole merge: released exactly at
    // the crossing's end, the robot accelerated into the merge bend and
    // clipped the far junction corner.
    const crossingHere = (input.crossings ?? []).some(
      (c) => s0 >= c.s0 - 3 && s0 <= c.s1 + 3,
    );
    if (crossingHere) {
      speedCap = Math.min(Math.max(speedCap, 1.3), 2.2);
    } else {
      speedCap = Math.max(speedCap, input.cruise * CORRIDOR_FLOOR_FRAC);
    }
    // The cap may only RISE gently (~2 m/s²): micro-dips from a conflict
    // flickering in and out otherwise read as throttle surges. Drops stay
    // instant — braking for a real reason is not jerk.
    this.capSmooth = Math.min(
      speedCap,
      this.capSmooth + CORRIDOR_CAP_RISE_MPS2 * input.dt,
    );
    speedCap = this.capSmooth;

    // --- 8) State, with dwell-time hysteresis. -------------------------------
    const atRobot = stations[0] ?? { s: s0, lo: -1, hi: 1 };
    const raw = this.rawState(input, blockedBy, bulge, effLen, pocket);
    this.applyDwell(raw, input.t);

    return {
      state: this.state,
      stations,
      length: effLen,
      pocket,
      amber,
      speedCap,
      conflictS,
      priorityId: bindingId ?? this.amberId,
      centerD: (atRobot.lo + atRobot.hi) / 2,
      // The PAINTED width: what the body may sweep, so edges the player
      // sees correspond to where the robot may actually reach.
      width: atRobot.hi - atRobot.lo + shape.r * 2,
      bulge,
      neutral: false,
      reason:
        blockedBy === "moving"
          ? `give way to ${bindingId}`
          : blockedBy === "static"
            ? oncomingActive
              ? "right side blocked, traffic active"
              : "right side blocked"
            : leanId !== null
              ? `letting ${leanId} pass`
              : bulge
                ? "passing a static block"
                : turn > CORRIDOR_TURN_TIGHT_RAD
                  ? "limited visibility (bend)"
                  : "open road",
    };
  }

  // -------------------------------------------------------------------------

  /** The research-window corridor: full road width, no reactions, cruise. */
  private neutralPlan(
    input: CorridorInput,
    s0: number,
    maxLen: number,
    need: number,
  ): CorridorPlan {
    const stations: CorridorStation[] = [];
    for (let s = s0; s <= s0 + maxLen + 1e-6; s += CORRIDOR_STATION_M) {
      const road = this.roadInterval(s, need, input);
      if (!road) break;
      stations.push({ s, lo: road.lo, hi: road.hi });
    }
    this.lenSmooth += clamp(
      maxLen - this.lenSmooth,
      -CORRIDOR_RETRACT_MPS * input.dt,
      CORRIDOR_EXTEND_MPS * input.dt,
    );
    this.amberId = null;
    this.lastAmber = null;
    this.lastPocket = null;
    this.state = "OPEN_RIGHT";
    this.capSmooth = Math.min(
      input.cruise,
      this.capSmooth + CORRIDOR_CAP_RISE_MPS2 * input.dt,
    );
    const atRobot = stations[0] ?? { s: s0, lo: -1, hi: 1 };
    return {
      state: "OPEN_RIGHT",
      stations,
      length: Math.min(this.lenSmooth, maxLen),
      pocket: null,
      amber: null,
      speedCap: this.capSmooth,
      conflictS: null,
      priorityId: null,
      centerD: (atRobot.lo + atRobot.hi) / 2,
      width: atRobot.hi - atRobot.lo + input.shape.r * 2,
      bulge: false,
      neutral: true,
      reason: "research window (neutral)",
    };
  }

  /**
   * The band at station s: road minus curb margins, static occupancy, and any
   * TIMELY moving occupancy, biased to a comfortable right-side window.
   *
   * `band: null` with `deadBy` set means a moving road user leaves nothing
   * viable — the give-way case; with `deadBy` null the block is static.
   * `leanBy` reports a moving cut that narrowed the chosen band without
   * killing it — the let-them-pass case.
   */
  private station(
    s: number,
    need: number,
    input: CorridorInput,
    staticCuts: ReadonlyArray<{ s: number; d: number; cut: number }>,
    moving: readonly Judged[],
    s0: number,
    vRef: number,
    allowBulge: boolean,
  ): {
    band: { lo: number; hi: number; flo: number; fhi: number } | null;
    deadBy: string | null;
    leanBy: string | null;
    bulged: boolean;
    tight: boolean;
  } {
    const none = { band: null, deadBy: null, leanBy: null, bulged: false, tight: false };
    const road = this.roadInterval(s, need, input);
    if (!road) return none;
    // Station intervals are CENTER-freedom ranges: `need` has already
    // subtracted the body and safety margin from both sides, so ANY
    // positive width is physically passable. These thresholds are about
    // steering comfort, not fit.
    const minW = 1.0;
    // A squeeze narrower than the comfortable band is still offered — slowly.
    // Alleys with a parked cart leave ~0.85 m of center freedom; refusing
    // it deadlocks a level designed around exactly that squeeze.
    const minTight = CORRIDOR_TIGHT_CLEAR_M * 2;

    // Keep-right window ANCHORED TO THE ROUTE, leaning right. Anchoring to
    // the road's right edge instead once steered the band into a junction's
    // cross-street and dragged the robot into the corner. On a pedestrian
    // crossing leg there is no "right" to keep — the band is the zebra,
    // symmetric about the route.
    const cross = inCrossing(input.crossings, s);
    let targetLo = Math.max(road.lo, cross ? -1.3 : -CORRIDOR_LEFT_MAX_M);
    let targetHi = Math.min(road.hi, cross ? 1.3 : CORRIDOR_RIGHT_MAX_M);
    if (targetHi - targetLo < minTight) {
      // The route line itself is off the drivable region (a fillet hugging a
      // junction's inner curb): slide the window to the nearest drivable side.
      if (road.lo > -CORRIDOR_LEFT_MAX_M) {
        targetLo = road.lo;
        targetHi = Math.min(road.hi, targetLo + CORRIDOR_SLIDE_WIDTH_M);
      } else {
        targetHi = road.hi;
        targetLo = Math.max(road.lo, targetHi - CORRIDOR_SLIDE_WIDTH_M);
      }
    }

    // Static blockers (props and stopped road users) near this s.
    const blocked: Array<[number, number]> = [];
    const p = input.path.point(s);
    for (const o of input.obstacles) {
      const iv = obstacleInterval(o, p, s, need);
      if (iv) blocked.push(iv);
    }
    for (const a of staticCuts) {
      if (Math.abs(a.s - s) < a.cut) {
        blocked.push([a.d - (a.cut - 0.6) - need, a.d + (a.cut - 0.6) + need]);
      }
    }

    // Timely moving occupancy: a cut counts only if the agent is there NOW
    // (imminent) or around the time the robot itself would reach this
    // station. Without the timing gate the robot waited for road users who
    // would be long gone before it arrived — the definition of an
    // unnecessary wait.
    const key = Math.round(s / CORRIDOR_STATION_M);
    const tR = (s - s0) / vRef;
    const movingHere: Array<[number, number, string]> = [];
    for (const j of moving) {
      for (const c of j.cuts.get(key) ?? []) {
        if (c.tA <= 1.5 || Math.abs(c.tA - tR) <= CORRIDOR_TTC_WINDOW_S) {
          movingHere.push([c.lo, c.hi, c.id]);
        }
      }
    }

    const pick = (
      cuts: ReadonlyArray<[number, number]>,
    ): { lo: number; hi: number; flo: number; fhi: number; bulged: boolean } | null => {
      const free = subtract([road.lo, road.hi], cuts).filter(([lo, hi]) => hi - lo >= minTight);
      for (const [lo, hi] of free) {
        // Substantive overlap only: a sliver grazing the window's edge is not
        // the keep-right band, it is the left-side pass wearing its clothes —
        // choosing it once walked the corridor around a car mid-yield.
        const ovW = Math.min(hi, targetHi) - Math.max(lo, targetLo);
        if (ovW >= minTight) {
          const bandLo = Math.max(lo, Math.min(targetLo, hi - minW));
          const bandHi = Math.min(hi, Math.max(targetHi, bandLo + minTight));
          return { lo: bandLo, hi: bandHi, flo: lo, fhi: hi, bulged: false };
        }
      }
      if (!allowBulge) return null;
      let best: [number, number] | null = null;
      for (const [lo, hi] of free) {
        if (hi <= targetLo + 0.2 && (best === null || hi > best[1])) best = [lo, hi];
      }
      return best ? { lo: best[0], hi: best[1], flo: best[0], fhi: best[1], bulged: true } : null;
    };

    // Without moving cuts first: that tells a dead station apart from a
    // statically blocked one, which is the difference between "give way"
    // (amber, wait, resume) and "blocked right" (wait for a chance to pass).
    const staticBand = pick(blocked);
    if (staticBand === null) return none;
    if (movingHere.length === 0) {
      return {
        band: staticBand,
        deadBy: null,
        leanBy: null,
        bulged: staticBand.bulged,
        tight: staticBand.hi - staticBand.lo < minW,
      };
    }
    const both = pick([...blocked, ...movingHere.map(([lo, hi]) => [lo, hi] as [number, number])]);
    if (both === null) {
      // A moving road user leaves nothing viable here: give way to whichever
      // cut overlaps the band that would otherwise have been chosen.
      const culprit =
        movingHere.find(([lo, hi]) => hi > staticBand.lo && lo < staticBand.hi) ?? movingHere[0]!;
      return { band: null, deadBy: culprit[2], leanBy: null, bulged: false, tight: false };
    }
    const narrowed = both.hi - both.lo < staticBand.hi - staticBand.lo - 0.15;
    const leanBy = narrowed
      ? (movingHere.find(([lo, hi]) => hi > staticBand.lo && lo < staticBand.hi)?.[2] ?? null)
      : null;
    return {
      band: both,
      deadBy: null,
      leanBy,
      bulged: both.bulged,
      tight: both.hi - both.lo < minW,
    };
  }

  /** Contiguous drivable d-interval around the route at s (cached: static). */
  private roadInterval(
    s: number,
    need: number,
    input: CorridorInput,
  ): { lo: number; hi: number } | null {
    const key = Math.round(s / 0.5);
    const hit = this.roadCache.get(key);
    if (hit) return hit.hi > hit.lo ? hit : null;
    const p = input.path.point(Math.round(s / 0.5) * 0.5);
    const rx = Math.cos(p.theta);
    const rz = -Math.sin(p.theta);
    const ok = (d: number): boolean =>
      input.network.clearance(p.x + rx * d, p.z + rz * d) >= need;
    let lo = 0;
    let hi = 0;
    if (!ok(0)) {
      // The route point itself may sit within `need` of a curb at a cut
      // junction corner — allow a SMALL recovery scan only. Anything
      // further is a different road across the curb: accepting it once
      // dragged the robot 12 m onto a parallel street and wedged it.
      let found = false;
      for (let d = PROBE_STEP; d <= 1.2; d += PROBE_STEP) {
        if (ok(d)) { lo = hi = d; found = true; break; }
        if (ok(-d)) { lo = hi = -d; found = true; break; }
      }
      if (!found) {
        this.roadCache.set(key, { lo: 1, hi: 0 });
        return null;
      }
    }
    const range = Math.min(PROBE_RANGE, p.halfWidth + 2.5);
    while (hi < range && ok(hi + PROBE_STEP)) hi += PROBE_STEP;
    while (lo > -range && ok(lo - PROBE_STEP)) lo -= PROBE_STEP;
    const iv = { lo, hi };
    this.roadCache.set(key, iv);
    return iv;
  }

  /**
   * Predict one moving road user: null (no interaction), "static" (slow
   * same-direction — a block to follow or pass, never an amber), or its
   * predicted occupancy as per-station cuts plus the amber polyline.
   */
  private judge(
    a: AgentState,
    speed: number,
    input: CorridorInput,
    s0: number,
    maxLen: number,
  ): Judged | "static" | null {
    // An agent already latched (the visible amber, or recently judged) skips
    // the entry gates — re-running them every step made the amber strobe as
    // the robot's own speed changed the timing.
    const latched =
      a.id === this.amberId || (this.conflictHold.get(a.id) ?? -Infinity) > input.t;
    const hint = this.sHintByAgent.get(a.id);
    const f0 = input.path.project(a.x, a.z, hint ?? s0, hint === undefined ? 60 : 10);
    this.sHintByAgent.set(a.id, f0.s);
    const along =
      (a.safetyVx * Math.sin(f0.theta) + a.safetyVz * Math.cos(f0.theta)) / speed;
    const alongRobot =
      (a.safetyVx * Math.sin(input.robotHeading) + a.safetyVz * Math.cos(input.robotHeading)) /
      speed;
    // Same direction by EITHER frame: at a junction the path tangent
    // rotates under an agent that is simply travelling with the robot.
    const sameDir = along > 0.7 || alongRobot > 0.7;
    // Entirely behind and travelling our way: ignore (mirror of the
    // governor's rear rule — a follower manages its own gap).
    if (f0.s < s0 - 1 && sameDir) return null;
    // Beside or ahead, same way, and not slower than the robot: it is
    // pulling away — overtakers included. It manages itself.
    if (sameDir && speed >= Math.max(2.5, input.robotSpeed - 0.5)) return null;
    if (sameDir) return "static"; // slow mover ahead: follow or pass around

    // Oncoming or crossing: sweep the intent velocity over the horizon and
    // carve every station its swept space touches. The sweep covers the gaps
    // BETWEEN samples too — at 5 m/s the samples are 2 m apart.
    const pts: Array<{ x: number; z: number }> = [];
    const cuts = new Map<number, MovingCut[]>();
    const cutR = a.radius + CORRIDOR_AMBER_MARGIN_M + input.shape.r + CORRIDOR_CURB_CLEAR_M;
    let touched = false;
    let sHint: number | null = f0.s;
    let prev: { s: number; d: number } | null = null;
    for (let t = 0; t <= CORRIDOR_HORIZON_S + 1e-6; t += CORRIDOR_PREDICT_STEP_S) {
      const x = a.x + a.safetyVx * t;
      const z = a.z + a.safetyVz * t;
      pts.push({ x, z });
      const f = input.path.project(x, z, sHint, 12);
      sHint = f.s;
      const cur = { s: f.s, d: f.d };
      const from = prev ?? cur;
      prev = cur;
      const sMin = Math.min(from.s, cur.s) - a.radius;
      const sMax = Math.max(from.s, cur.s) + a.radius;
      if (sMax < s0 - 1 || sMin > s0 + maxLen + CORRIDOR_CONFLICT_CLEAR_M) continue;
      const lo = Math.min(from.d, cur.d) - cutR;
      const hi = Math.max(from.d, cur.d) + cutR;
      for (
        let k = Math.ceil((sMin - CORRIDOR_STATION_M / 2) / CORRIDOR_STATION_M);
        k * CORRIDOR_STATION_M <= sMax + CORRIDOR_STATION_M / 2;
        k++
      ) {
        let list = cuts.get(k);
        if (!list) cuts.set(k, (list = []));
        list.push({ lo, hi, id: a.id, tA: t });
        touched = true;
      }
    }
    // Does any predicted cut touch the keep-right window? Only those may
    // lean or end the band — far-lane traffic must not bend the corridor.
    let near = false;
    if (touched) {
      outer: for (const list of cuts.values()) {
        for (const c of list) {
          if (c.hi > -CORRIDOR_LEFT_MAX_M - 0.6 && c.lo < CORRIDOR_RIGHT_MAX_M + 0.6) {
            near = true;
            break outer;
          }
        }
      }
    }
    if (near || (latched && touched)) {
      return {
        id: a.id,
        amber: { id: a.id, pts, halfWidth: a.radius + CORRIDOR_AMBER_MARGIN_M },
        cuts,
      };
    }
    // Far-lane or beyond-corridor, but coming down our road: it must still
    // FORBID THE PASS (booked with empty cuts — it cannot lean the band or
    // show a false amber). Without this the robot once committed to the
    // left lane with an oncoming motorbike two seconds away and left the
    // governor to rescue the manoeuvre; its lane sits exactly where the
    // bulge goes, which is why window distance alone cannot excuse it.
    const towardUs =
      (a.safetyVx * Math.sin(f0.theta) + a.safetyVz * Math.cos(f0.theta)) / speed < -0.3;
    // Judged by TIME TO MEET, not distance: an oncoming vehicle six seconds
    // out leaves room to complete a pass — forbidding it parked the robot
    // to stare at a slow motorbike half a street away.
    const closing = speed + Math.min(input.robotSpeed, input.cruise * 0.5) + 0.1;
    const tMeet = (f0.s - s0) / closing;
    if (towardUs && f0.s > s0 && tMeet < 4.5) {
      return {
        id: a.id,
        amber: { id: a.id, pts, halfWidth: a.radius + CORRIDOR_AMBER_MARGIN_M },
        cuts: new Map(),
      };
    }
    return null;
  }

  // -------------------------------------------------------------------------

  private rawState(
    input: CorridorInput,
    blockedBy: "static" | "moving" | null,
    bulge: boolean,
    effLen: number,
    pocket: CorridorPlan["pocket"],
  ): CorridorState {
    if (input.governor === "EMERGENCY_STOP") return "EMERGENCY_STOP";
    if (blockedBy === "moving" && pocket !== null && effLen <= CORRIDOR_POCKET_LEN_M + 1.5) {
      return "WAITING_POCKET";
    }
    if (blockedBy === "static" && !bulge) return "BLOCKED_RIGHT";
    if (bulge) {
      this.returnSince = null;
      return "PASSING_STATIC_OBSTACLE";
    }
    if (this.state === "PASSING_STATIC_OBSTACLE" || this.state === "RETURN_RIGHT") {
      // The bulge has ended; report RETURN_RIGHT until the robot itself is
      // back in the keep-right band, so telemetry can see the whole maneuver.
      if (this.returnSince === null) this.returnSince = input.t;
      const back = Math.abs(input.robotD) < CORRIDOR_RETURN_TOL_M * 3;
      if (!back || input.t - this.returnSince < 0.8) return "RETURN_RIGHT";
      this.returnSince = null;
    }
    if (blockedBy !== null || effLen < CORRIDOR_MAX_LEN_M * 0.55) return "CAUTION";
    return "OPEN_RIGHT";
  }

  /**
   * Escalation (toward stopping) is immediate — hesitating on safety to avoid
   * flicker would be backwards. De-escalation waits out the dwell time, which
   * is where the no-flicker guarantee actually lives.
   */
  private applyDwell(raw: CorridorState, t: number): void {
    if (raw === this.state) return;
    const rank: Record<CorridorState, number> = {
      OPEN_RIGHT: 0,
      RETURN_RIGHT: 1,
      CAUTION: 2,
      PASSING_STATIC_OBSTACLE: 3,
      BLOCKED_RIGHT: 4,
      WAITING_POCKET: 5,
      EMERGENCY_STOP: 6,
    };
    const escalating = rank[raw] > rank[this.state];
    if (escalating || t - this.stateSince >= CORRIDOR_DWELL_S) {
      this.state = raw;
      this.stateSince = t;
    }
  }
}

// ---------------------------------------------------------------------------

/** Subtract closed intervals from one interval; returns the remaining parts. */
function subtract(
  base: [number, number],
  cuts: ReadonlyArray<[number, number]>,
): Array<[number, number]> {
  let parts: Array<[number, number]> = [base];
  for (const [clo, chi] of cuts) {
    const next: Array<[number, number]> = [];
    for (const [lo, hi] of parts) {
      if (chi <= lo || clo >= hi) {
        next.push([lo, hi]);
        continue;
      }
      if (clo > lo) next.push([lo, clo]);
      if (chi < hi) next.push([chi, hi]);
    }
    parts = next;
  }
  return parts;
}

/** An obstacle AABB's d-interval at a station, or null when it misses it. */
function obstacleInterval(
  o: Obstacle,
  p: { x: number; z: number; theta: number },
  s: number,
  need: number,
): [number, number] | null {
  // Project the four corners into the station's frame; the obstacle bears on
  // this station when its along-track extent spans it.
  const fx = Math.sin(p.theta);
  const fz = Math.cos(p.theta);
  const rx = Math.cos(p.theta);
  const rz = -Math.sin(p.theta);
  let sMin = Infinity;
  let sMax = -Infinity;
  let dMin = Infinity;
  let dMax = -Infinity;
  for (const cx of [-o.halfW, o.halfW]) {
    for (const cz of [-o.halfD, o.halfD]) {
      const wx = o.x + cx - p.x;
      const wz = o.z + cz - p.z;
      const along = wx * fx + wz * fz;
      const lat = wx * rx + wz * rz;
      sMin = Math.min(sMin, along);
      sMax = Math.max(sMax, along);
      dMin = Math.min(dMin, lat);
      dMax = Math.max(dMax, lat);
    }
  }
  if (sMax < -CORRIDOR_STATION_M / 2 || sMin > CORRIDOR_STATION_M / 2) return null;
  return [dMin - need, dMax + need];
}

function agentHalfLen(a: AgentState): number {
  return a.body.length > 0 ? Math.max(...a.body.map(Math.abs)) : 0;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * The corridor's d-interval at path progress s (stations lerped; the pocket
 * wins where it overlaps, because waiting is meant to be roomier than
 * driving), or null outside the corridor's s-range.
 */
export function intervalAt(
  plan: CorridorPlan,
  s: number,
): { lo: number; hi: number } | null {
  if (plan.pocket && s >= plan.pocket.s0 - 0.3 && s <= plan.pocket.s1 + 0.3) {
    return { lo: plan.pocket.lo, hi: plan.pocket.hi };
  }
  const st = plan.stations;
  if (st.length === 0) return null;
  if (s <= st[0]!.s) return { lo: st[0]!.lo, hi: st[0]!.hi };
  const last = st[st.length - 1]!;
  if (s >= last.s) {
    return s > last.s + CORRIDOR_STATION_M ? null : { lo: last.lo, hi: last.hi };
  }
  for (let i = 1; i < st.length; i++) {
    if (st[i]!.s >= s) {
      const a = st[i - 1]!;
      const b = st[i]!;
      // Disjoint neighbours happen where the band jumps at a junction corner;
      // lerping across the gap would run the band through the curb.
      if (a.hi < b.lo || b.hi < a.lo) {
        const u0 = (s - a.s) / Math.max(1e-6, b.s - a.s);
        return u0 < 0.5 ? { lo: a.lo, hi: a.hi } : { lo: b.lo, hi: b.hi };
      }
      const u = (s - a.s) / Math.max(1e-6, b.s - a.s);
      return { lo: a.lo + (b.lo - a.lo) * u, hi: a.hi + (b.hi - a.hi) * u };
    }
  }
  return null;
}

/** The painted lane's edges at path progress s (stations lie on a uniform
 * grid from 0, so this is an index + lerp), or null off the route. */
export function guideAt(
  guide: readonly CorridorStation[],
  s: number,
): { lo: number; hi: number } | null {
  if (guide.length === 0) return null;
  const f = s / CORRIDOR_STATION_M;
  const i = Math.floor(f);
  if (i < 0) return guide[0]!;
  if (i >= guide.length - 1) return guide[guide.length - 1]!;
  const u = f - i;
  const a = guide[i]!;
  const b = guide[i + 1]!;
  return { lo: a.lo + (b.lo - a.lo) * u, hi: a.hi + (b.hi - a.hi) * u };
}
